import React, { useState } from 'react';
import { Badge } from './Common';
import { useAuth } from '../context/AuthContext';

// What an account can already do with NO groups assigned. Worth stating plainly:
// the MSP / client choice on the user form is itself a permission, not just a filter.
export const BASELINE_ACCESS = [
  {
    audience: 'Client',
    label: 'A client account with no groups',
    points: [
      'Sees its own client\'s engagements (except ones still in New) and their findings, and can open a New engagement from a direct link.',
      'Can already edit those engagements — dates, tester assignment, scoping fields, notes and scope items.',
      'Can already move an engagement\'s status, including straight to Complete. Only New, Cancelled and In Review → Complete are withheld.',
      'Can create, edit, delete and bulk-import that client\'s applications, and upload files to its engagements.',
      'Can generate reports for its own client\'s engagements.',
      'Cannot create engagements, and cannot create or edit findings.',
    ],
  },
  {
    audience: 'MSP',
    label: 'An MSP account with no groups',
    points: [
      'Sees an empty engagement list — MSP visibility comes from the groups below, so an MSP user with no groups has no useful landing page.',
      'Sees every client and every user in the system.',
      'Can create and edit any client record.',
      'Can edit the global vulnerability template library and the scanner payload settings, both of which are shared by every tenant.',
      'Can generate a report for any engagement, for any client.',
    ],
  },
];

// Documentation for the built-in permission groups. `grants` describes what the
// group actually unlocks; `nav` lists the sidebar items it reveals (see
// components/Layout.js). Keep this in sync with the permission checks in
// backend/routes/* — it is user-facing documentation, not enforcement.
export const ROLE_SECTIONS = [
  {
    label: 'Administration',
    blurb: 'Running the platform and the engagement pipeline.',
    roles: [
      {
        name: 'Super Admins',
        audience: 'MSP',
        summary: 'Unrestricted access, plus the destructive actions no other group has.',
        grants: [
          'Passes every permission check in the system — a Super Admin never needs any other group.',
          'Sees all engagements, including ones not marked third party, which other MSP groups cannot see.',
          'The only group that can delete an engagement, with all of its findings, or delete a user.',
          'The only group that can mark a user as MSP staff or move a user between clients.',
          'The only group outside a client that can see or modify that client\'s own report and vulnerability templates.',
          'Manages groups, SSO, notification channels and the activity log.',
          'Sets the Third Party flag when creating an engagement.',
        ],
        nav: ['Group Management', 'Activity Log', 'SSO Settings', 'Playbook Templates', 'Clients', 'Auto Scanner'],
      },
      {
        name: 'Engagement Admins',
        audience: 'Both',
        summary: 'Runs engagements day to day: creates them, assigns testers, manages findings.',
        grants: [
          'Creates, edits and CSV-imports engagements, and assigns testers.',
          'Full control of findings — create, edit, delete, change status, import from templates or Burp.',
          'Creates and edits applications, and imports them from CSV.',
          'Client-side: authors and clones vulnerability templates in their own client\'s library.',
          'Edits the test-case playbook on any engagement.',
          'Moves engagements through status, and can return one to New.',
          { text: 'No longer reaches the Auto Scanner — automated scanning is Super Admins only.',
            clientText: 'Does not reach the Auto Scanner; automated scanning is not available to this group.' },
        ],
        nav: [],
        note: 'Scope depends on the account type. An MSP-side Engagement Admin works across all clients but their engagement list shows only third-party engagements. A client-side Engagement Admin sees their own client only, including engagements still in New. Neither can cancel an engagement or move one from In Review to Complete.',
        clientNote: 'Sees your organisation only, including engagements still in New. Cannot cancel an engagement, or move one from In Review to Complete.',
      },
      {
        name: 'Requestors',
        audience: 'Both',
        summary: 'Asks for penetration testing and follows it. Held on its own, the account’s entire access.',
        grants: [
          'Opens the Requests page and reveals its sidebar link.',
          'Submits a request for testing, which creates an engagement in Requested status.',
          'Follows the tests they requested — their status, dates and assigned tester.',
        ],
        nav: ['Requests'],
        note: 'This group confines rather than grants. Held on its own it is the account’s whole access — the Requests page and nothing else in the application, refused at the API rather than merely hidden from the menu. A submitted request cannot be edited by the person who raised it, which follows from the same restriction. Held alongside any group that grants writing it stops confining and simply adds the request form, so the two are offered as alternatives rather than as a combination.',
      },
      {
        name: 'Application Managers',
        audience: 'Both',
        summary: 'Owns the application inventory.',
        clientSummary: 'Owns the application inventory. The only group that can open the Applications page.',
        grants: [
          'Opens the Applications page and reveals its sidebar link.',
          'Creates, edits, imports and deletes applications.',
          'Maintains an application’s scope items and notes.',
          'Marks applications out of scope, which removes them from compliance figures by default.',
        ],
        nav: ['Applications'],
        note: 'Client-side holders manage their own organisation’s inventory only. This is what governance compliance is measured against, so the group carries more weight than it looks: an application missing from the inventory, or wrongly marked out of scope, is an application the compliance reports say nothing about. People who create engagements can still choose an application from the dropdown without this group — they just cannot change the inventory itself.',
      },
      {
        name: 'Technical Managers',
        audience: 'Both',
        summary: 'Holds the two engagement status transitions that need sign-off.',
        grants: [
          { text: 'Moves an engagement from In Review to Complete — only this group and Super Admins can.',
            clientText: 'Moves an engagement from In Review to Complete — the only group that can.' },
          { text: 'Cancels an engagement, which also zeroes its billing — again only this group and Super Admins.',
            clientText: 'Cancels an engagement, which also zeroes its billing — again the only group that can.' },
          'Recorded as the technical manager on the engagement when it is completed.',
        ],
        nav: [],
        note: 'Grants no access of its own, so it does not work alone: the user still needs edit rights on the engagement from another group (usually Engagement Admins) or to be the assigned tester. On its own, the approval power cannot be exercised.',
      },
      {
        name: 'MSP Billing Admins',
        audience: 'MSP',
        summary: 'Owns the commercial record across every client.',
        grants: [
          'Sets billing amounts, marks engagements billed, and records service orders, for any client.',
          'Full control of statements of work, including credit pools and uploading the signed contract document.',
          'Reads the cross-client billing summary and the unbilled queue.',
        ],
        nav: ['Billing', 'Statements of Work'],
        note: 'This is the provider-side billing group and it deliberately sees every client. A client that needs to manage its own commercials gets Client Billing Admins instead. Grants no engagement editing at all, not even the billing amount on the engagement itself.',
      },
      {
        name: 'Client Billing Admins',
        audience: 'Client',
        summary: 'The same commercial control, limited to their own client.',
        grants: [
          'Sets billing amounts, marks engagements billed and records service orders, for their own client only.',
          'Manages their own client\'s statements of work, including credits and the signed contract document.',
          'Reads the billing summary and unbilled queue for their own client.',
        ],
        nav: ['Billing', 'Statements of Work'],
        note: 'Every billing query is filtered to the holder\'s client, so another client\'s figures are never returned — asking for one by id is refused. The group needs a client to be useful: an account without one is refused outright rather than falling through to everything. Grants no engagement editing.',
      },
    ],
  },
  {
    label: 'Testing',
    blurb: 'For the people doing the actual testing work.',
    roles: [
      {
        name: 'MSP Penetration Testers',
        audience: 'MSP',
        summary: 'Provider-side tester working across client engagements.',
        grants: [
          'Sees the engagements they are the assigned tester on — and only those.',
          'Creates and edits findings on engagements they are assigned to.',
          'Changes the status of any finding.',
          'Uploads evidence and files, and imports findings from Burp.',
          'Uses the AttackPath Proxy tooling without needing the proxy group.',
          'Reads the shared vulnerability template library, but cannot author in it.',
        ],
        nav: [],
        note: 'Assignment is the grant, so the engagement list, the dashboard figures and the findings list all show the same set: work handed to this tester. Engagements not assigned to them cannot be opened by address either. Cannot create engagements, and cannot open the Applications page.',
      },
      {
        name: 'Penetration Testers',
        audience: 'Client',
        summary: 'Client-side tester, scoped to the engagements assigned to them.',
        grants: [
          'Sees the engagements they are the assigned tester on — and only those.',
          'Creates, edits and deletes findings, and attaches evidence, on those engagements.',
          'Edits the test-case playbook on those engagements.',
          'Views and edits engagement credentials, including rotating stored secrets.',
          'Imports findings from Burp, and uses the AttackPath Proxy tooling.',
          'Authors and clones vulnerability templates in their own client\'s library.',
        ],
        nav: ['Vuln Templates'],
        note: 'Assignment is the grant. An engagement this user is not the assigned tester on is absent from their list, from their dashboard figures and from their findings, and cannot be opened by its address either — including engagements belonging to their own organisation. Explicitly blocked from creating engagements, and from opening the Applications page. Someone who needs a wider view of their organisation should be paired with Engagement Admins, which takes precedence.',
      },
      {
        name: 'AttackPath Proxy',
        audience: 'Both',
        summary: 'Unlocks the local intercepting proxy and its traffic sync.',
        grants: [
          'Downloads the proxy installer, compose file and pre-filled configuration.',
          'Pushes intercepted traffic, proxy findings and site maps back to an engagement, and pulls scope rules.',
        ],
        nav: ['AttackPath Proxy'],
        note: 'Both tester groups already have this access, so the group is mainly for non-testers who need the proxy. Treat the generated configuration file as a secret: it embeds the user\'s own live session token and a shared container-registry credential.',
      },
    ],
  },
  {
    label: 'Reporting',
    blurb: 'Producing a report and designing the templates behind it are separate.',
    roles: [
      {
        name: 'Report Generators',
        audience: 'Both',
        summary: 'Required to produce a report — nobody else can, whatever else they hold.',
        grants: [
          'Generates the report for an engagement, in Word or PDF, and shows the controls for it.',
        ],
        nav: [],
        note: 'This is enforced at the API, not just in the interface: without this group the request is refused, and Super Admins are the only exception. It does not follow from editing an engagement, so Engagement Admins, Client Admins and assigned testers all need it granted explicitly. It grants nothing else — pair it with a group that provides the underlying access to the engagement.',
        clientNote: 'This is enforced at the API, not just in the interface: without this group the request is refused. It does not follow from editing an engagement, so Engagement Admins, Client Admins and assigned testers all need it granted explicitly. It grants nothing else — pair it with a group that provides the underlying access to the engagement.',
      },
      {
        name: 'Report Admins',
        audience: 'Both',
        summary: 'Designs and maintains the report templates themselves.',
        grants: [
          'Creates, edits, clones, imports, exports and deletes report templates.',
        ],
        mspGrants: [
          'MSP-side: edits any template, including any client\'s, and controls which clients a template is shared with.',
        ],
        nav: ['Report Templates'],
        note: 'A client-side Report Admin can only edit their own client\'s templates — not MSP templates shared with them, and not template sharing. Reading and exporting templates needs no group at all, so this group is specifically about authoring.',
        clientNote: "Edits your own organisation's templates. Templates shared with you from elsewhere are read-only, as is who they are shared with. Reading and exporting templates needs no group at all, so this group is specifically about authoring.",
      },
      {
        name: 'AI KB Admins',
        audience: 'MSP',
        summary: 'Curates the AI knowledge base behind finding and report assistance.',
        grants: [
          'Manages AI knowledge base files and training data.',
        ],
        nav: ['AI Knowledge Base'],
      },
    ],
  },
  {
    label: 'Client access',
    blurb: 'For users belonging to a client organisation. A client account only ever sees its own client\'s data.',
    roles: [
      {
        name: 'Client Admins',
        audience: 'Client',
        summary: 'The customer\'s own administrator.',
        grants: [
          'Creates and edits users within their own client, and resets their passwords.',
          'Sees all of their client\'s engagements, including ones still in New, and can return one to New.',
          'Assigns testers and edits their client\'s engagements.',
          'Manages their client\'s integrations, and reads their own statements of work including contract amounts.',
        ],
        nav: ['User Management', 'Integrations', 'Statements of Work'],
        note: 'When adding users they are offered only the client-side groups, which includes Client Admins itself — so one client admin can promote another. They cannot see the MSP staff toggle or move a user to another client. Note they cannot create or edit findings themselves; that needs Penetration Testers or Engagement Admins. They also cannot edit their own client record, manage SSO, or see the activity log.',
        clientNote: "When adding users they are offered the groups available to your organisation, which includes Client Admins itself — so one admin can promote another. Users always belong to your organisation and cannot be moved out of it. They cannot create or edit findings themselves; that needs Penetration Testers or Engagement Admins. They also cannot edit the organisation record, manage SSO, or see the activity log.",
      },
      {
        name: 'Client Viewers',
        audience: 'Client',
        summary: 'Read-only access to their own engagements and what came out of them.',
        grants: [
          "Reads their own client's engagement records.",
          'Reads the scope of those engagements — what was agreed to be tested.',
          'Lists and downloads the documents attached to them.',
          'Lists and downloads the reports produced from them.',
        ],
        nav: [],
        note: "Read-only, and enforced at the API rather than hidden in the interface: every write is refused — no scope changes, no uploads, no notes, no report generation — and so is every other area, including applications, statements of work, billing and user management. The findings list is deliberately not included: it is the working set, holding drafts and severities still being agreed, and the settled version of that work reaches the client as a report. This group only makes an account read-only when it is the user's sole group — it restricts rather than grants, so pairing it with Client Admins, Engagement Admins or Penetration Testers gives that group's access back. Signing in and changing your own password are always allowed.",
      },
    ],
  },
];

const AUDIENCE_STYLE = {
  MSP: { background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa' },
  Client: { background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' },
  Both: { background: 'rgba(34, 197, 94, 0.15)', color: '#4ade80' },
};

function AudienceTag({ audience }) {
  const s = AUDIENCE_STYLE[audience] || AUDIENCE_STYLE.Both;
  return (
    <span style={{
      ...s, padding: '2px 8px', borderRadius: 10, fontSize: 11,
      fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {audience === 'Both' ? 'MSP or Client' : audience + ' only'}
    </span>
  );
}

const SECTION_HEADING = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--text-secondary)', paddingBottom: 4,
  borderBottom: '1px solid var(--border)', marginBottom: 8,
};

/**
 * Collapsible reference explaining what each permission group does.
 * `extraGroups` are groups that exist in the database but are not documented
 * above (for example ones created via Group Management); they are listed with
 * whatever description was saved with them.
 */
export default function RoleReference({ extraGroups = [] }) {
  const [expanded, setExpanded] = useState(false);
  const { isMsp } = useAuth();

  // A client user only ever sees client-side groups on the user form, so the
  // MSP-side roles are noise to them — and describing MSP internals is not
  // theirs to read. Roles carry an audience, so filter on that.
  const clientOnly = !isMsp;
  const baseline = clientOnly
    ? BASELINE_ACCESS.filter(b => b.audience === 'Client')
    : BASELINE_ACCESS;
  // Filtering whole roles is not enough. A role marked "Both" still describes
  // how it behaves on the MSP side, and that is not a client's to read, so the
  // text itself is resolved per audience: mspGrants are dropped and clientNote
  // replaces note. The AudienceTag goes too -- a client has no second audience
  // to be told apart from.
  const grantText = (g, forClient) =>
    typeof g === 'string' ? g : (forClient && g.clientText) || g.text;

  const resolve = (role) => clientOnly
    ? {
        ...role,
        summary: role.clientSummary || role.summary,
        grants: role.grants.map(g => grantText(g, true)),
        note: role.clientNote || role.note,
      }
    : {
        ...role,
        grants: [...role.grants, ...(role.mspGrants || [])].map(g => grantText(g, false)),
      };

  const sections = ROLE_SECTIONS
    .map(section => ({
      ...section,
      roles: (clientOnly
        ? section.roles.filter(r => r.audience === 'Client' || r.audience === 'Both')
        : section.roles).map(resolve),
    }))
    .filter(section => section.roles.length > 0);

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      background: 'var(--bg-card)', marginBottom: 16, overflow: 'hidden',
    }}>
      <button onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '12px 16px', background: 'none', border: 'none',
          cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit',
        }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
          <span>❓</span> What do these roles do?
        </span>
        <span style={{
          fontSize: 12, color: 'var(--text-secondary)',
          transform: expanded ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform 0.2s',
        }}>▼</span>
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 16 }}>
            Permissions come entirely from the groups assigned to a user — the older role field is no
            longer used anywhere. Groups add up, so a user gets the combined access of every group they
            hold{clientOnly ? '' : ', and Super Admins bypass every check regardless of their other groups'}.
            {/* The MSP/client split is internal: a client reads about the groups
                they can actually assign, not about how the same group behaves
                for staff on the other side of the relationship. */}
            {!clientOnly && (
              <div style={{ marginTop: 8 }}>
                Two independent things decide what someone can do: the <strong>groups</strong> they hold, and
                whether the account is <strong>MSP staff</strong> or belongs to a <strong>client</strong>. The
                same group behaves differently on each side — an MSP Engagement Admin works across clients on
                third-party engagements, while a client Engagement Admin only ever sees their own organisation.
              </div>
            )}
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={SECTION_HEADING}>Start here: access before any group</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
              Groups only ever add to this. Read it first — it is the part that surprises people.
            </div>
            {baseline.map(b => (
              <div key={b.label} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{b.label}</div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                  {b.points.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            ))}
          </div>

          {sections.map(section => (
            <div key={section.label} style={{ marginBottom: 20 }}>
              <div style={SECTION_HEADING}>{section.label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>{section.blurb}</div>

              {section.roles.map(role => (
                <div key={role.name} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <Badge>{role.name}</Badge>
                    {!clientOnly && <AudienceTag audience={role.audience} />}
                  </div>
                  <div style={{ fontSize: 13, marginBottom: role.grants.length ? 6 : 0 }}>{role.summary}</div>
                  {role.grants.length > 0 && (
                    <ul style={{ margin: '0 0 6px', paddingLeft: 20, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                      {role.grants.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  )}
                  {role.nav.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      Reveals in the sidebar: {role.nav.join(', ')}
                    </div>
                  )}
                  {role.note && (
                    <div style={{
                      fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-input)',
                      borderRadius: 6, padding: '6px 10px', lineHeight: 1.5,
                    }}>
                      {role.note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {extraGroups.length > 0 && (
            <div>
              <div style={SECTION_HEADING}>Other groups</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Groups added through Group Management. What they allow depends on how they are used.
              </div>
              {extraGroups.map(g => (
                <div key={g.name} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
                  <Badge>{g.name}</Badge>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{g.description || 'No description.'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
